import { DashboardModuleService } from '../../dashboard/services/dashboard-module.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { PERMISSION_TYPE } from '../enums/common.enum';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as PermissionJson from './../../../../assets/Json/permission.json';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { ApiCallService } from 'src/app/services/api-call.service';
import { API_URLS } from 'src/app/core/api-url.enum';
import get from 'lodash/get';
import { CommonService } from './common.service';
import { ROUTE_UNIQUE_KEYS } from 'src/app/constants/constant';
import { AuthService } from 'src/app/core/auth.service';
import { environment } from '@environments/environment';
@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private userData$ = new BehaviorSubject<any>(null);
  private permissions = PermissionJson;
  destroy$ = new Subject<void>();

  private readonly isBrowser: boolean;

  constructor(
    private readonly dashboardService: DashboardModuleService,
    private readonly apiCallService: ApiCallService,
    private readonly authService: AuthService,
    private readonly cs: CommonService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.dashboardService.userProfileSubscription()
    .pipe(takeUntil(this.destroy$))
    .subscribe((res) => {
      this.userData$.next(res);
    });
  }
  checkDelegateUserPermission(
    callbackFn: Function,
    path: String | null = null,
    action: PERMISSION_TYPE | null = null,
    checkDelegate = false,
    checkDelegatedUser = true,
    showNoPermissionError = true
  ) {
    this.hasPermission(path, action, checkDelegate, checkDelegatedUser)
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (showNoPermissionError) {
          hasPermission ? this.cs.showNoPermissionError() : callbackFn();
        } else {
          hasPermission && callbackFn();
        }
      });
  }

  hasPermission(
    path: any,
    action:
      | PERMISSION_TYPE.READ
      | PERMISSION_TYPE.WRITE
      | null = PERMISSION_TYPE.READ,
    checkDelegate = false,
    checkDelegatedUser = false
  ): Observable<boolean> {
    if (!this.isBrowser) {
      return of(true);
    }
    return this.userData$.pipe(
      filter((userData) => !!userData && userData.sk),
      map((userData) => {
        if (checkDelegatedUser) {
          return !!userData.delegate_user?.type?.length;
        }
        if (checkDelegate) {
          return this.checkDelegatePermission(userData, path, action);
        }
        return this.checkUserPermission(userData, path, action);
      })
    );
  }

  checkUserPermissions(
    userData: any,
    path: string,
    action: PERMISSION_TYPE,
    delegatePath?: Array<string>,
    isDelegatedUser?: boolean,
    checkMyProfile?: boolean,
    routeData?: any
  ): Observable<boolean> {
    if (!this.authService.isAuthenticated()) {
      return of(true);
    }
    if (userData?.sk) {
      return of(
        this.checkUserPermission(
          userData,
          path,
          action,
          delegatePath,
          isDelegatedUser,
          checkMyProfile,
          routeData
        )
      );
    }
    return this.apiCallService
      .get(
        `${API_URLS.users}?host_name=${environment.websiteURL}`,
        true
      )
      .pipe(
        switchMap((res) => {
          if (res.status) {
            return of(
              this.checkUserPermission(
                { ...res.data, delegate_user: res.delegate_user },
                path,
                action,
                delegatePath,
                isDelegatedUser,
                checkMyProfile,
                routeData
              )
            );
          } else {
            console.error('API response error:', res.message);
            return of(false);
          }
        }),
        catchError((err) => {
          console.error('Error in API call:', err);
          return of(false);
        })
      );
  }
  checkDelegatePermission(
    userData: any,
    path: string,
    action: PERMISSION_TYPE
  ) {
    if (get(userData, 'delegate_user.type.length', 0) > 0 && path) {
      const delegateUser = userData.delegate_user;
      let hasPermission = false;
      delegateUser.type.forEach((type) => {
        type = type.toUpperCase();
        const permission = delegateUser.permission?.[type] || {};
        if (permission[path]?.[action]) {
          hasPermission = true;
        }
      });
      return hasPermission;
    }
    return true;
  }

  getUserIdFromRoute(checkMyProfile, routeData) {
    let userId = null;
    if (checkMyProfile && routeData.routeKey) {
      switch (routeData.routeKey) {
        case ROUTE_UNIQUE_KEYS.STORE_EVENT_CALENDAR:
          userId = routeData.queryParams?.userId;
          break;
        case ROUTE_UNIQUE_KEYS.STORE_VIP_OFFERS:
          userId = routeData.routeParams?.id;
          break;
        case ROUTE_UNIQUE_KEYS.ALL_MERCHANDISE:
          userId = routeData.routeParams?.email;
          break;
      }
    }
    return userId;
  }
  checkUserPermission(
    userData: any,
    path: string,
    action: PERMISSION_TYPE,
    delegatePath?: Array<string>,
    isDelegatedUser?: boolean,
    checkMyProfile?: boolean,
    routeData?: any
  ): boolean {
    let hasDelegatePermission = false;
    const userId = this.getUserIdFromRoute(checkMyProfile, routeData);
    if (
      userId &&
      (userId === userData?.user_name || userId.includes(userData?.user_name))
    ) {
      return true;
    }

    //check user is in delegate mode
    if (isDelegatedUser) {
      return !userData.delegate_user?.type?.length;
    }
    if (delegatePath?.length) {
      delegatePath.forEach((pathItem) => {
        const delegatePermission = this.checkDelegatePermission(
          userData,
          pathItem,
          PERMISSION_TYPE.READ
        );
        if (delegatePermission) {
          hasDelegatePermission = true;
        }
      });
    } else {
      hasDelegatePermission = true;
    }

    const permissionObj = userData?.is_creator
      ? this.permissions.CREATOR
      : this.permissions.FAN;
    const userTypePerm = path ? (permissionObj[path]?.[action] ?? false) : true;
    return userTypePerm && hasDelegatePermission;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
